/**
 * Workflow View Page
 * 
 * Displays workflow details (metadata + package preview).
 * Placeholder for MVP - will be enhanced in future iterations.
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';


interface WorkflowViewProps {
  tenantId: string;
  apiBaseUrl: string;
  authToken: string;
}

export const WorkflowView: React.FC<WorkflowViewProps> = ({ tenantId, apiBaseUrl, authToken }) => {
  const { workflowId } = useParams<{ workflowId: string }>();
  const navigate = useNavigate();
  
  const [workflow, setWorkflow] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchWorkflow();
  }, [workflowId]);

  const fetchWorkflow = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(
        `${apiBaseUrl}/api/workflows/${workflowId}?tenantId=${encodeURIComponent(tenantId)}`,
        {
          headers: {
            'Authorization': `Bearer ${authToken}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch workflow: ${response.statusText}`);
      }

      const data = await response.json();
      setWorkflow(data);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = () => {
    navigate(`/workflows/${workflowId}/edit`);
  };

  const handleBack = () => {
    navigate('/workflows');
  };

  const secBtn = 'px-4 py-2 bg-white border border-gray-300 text-label font-medium text-gray-700 rounded-md hover:bg-gray-50 transition-colors duration-fast';
  const priBtn = 'px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white text-label font-semibold rounded-md transition-colors duration-fast';
  const sectionCard = 'bg-white rounded-lg border border-gray-200 shadow-xs p-6';

  if (loading) return <div className="min-h-screen bg-surface-app flex items-center justify-center"><p className="text-body text-gray-400">Loading workflow…</p></div>;

  if (error) return (
    <div className="min-h-screen bg-surface-app flex flex-col items-center justify-center gap-4">
      <p className="text-body text-error">Error: {error}</p>
      <button type="button" onClick={handleBack} className={secBtn}>Back to List</button>
    </div>
  );

  if (!workflow) return (
    <div className="min-h-screen bg-surface-app flex items-center justify-center"><p className="text-body text-error">Workflow not found</p></div>
  );

  const { metadata, package: pkg } = workflow;
  const tags = JSON.parse(metadata.tags || '[]');
  const dlRow = (lbl: string, val: React.ReactNode) => (
    <div className="flex gap-3 py-2.5 border-b border-gray-100 last:border-0">
      <span className="text-label font-medium text-gray-500 w-36 flex-shrink-0">{lbl}</span>
      <span className="text-label text-gray-900 flex-1">{val}</span>
    </div>
  );

  return (
    <div className="min-h-screen bg-surface-app">
      {/* Header */}
      <div className="bg-surface-panel border-b border-gray-200 px-8 py-4">
        <div className="max-w-screen-xl mx-auto flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <button type="button" onClick={() => navigate('/')} className={secBtn}>← Studio</button>
            <button type="button" onClick={handleBack} className="text-label text-gray-500 hover:text-gray-900 transition-colors duration-fast">← Back to List</button>
            <h1 className="text-2xl font-semibold text-gray-900">{metadata.displayName}</h1>
          </div>
          <button type="button" onClick={handleEdit} className={priBtn}>Edit Presentation</button>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-screen-xl mx-auto px-8 py-6 flex flex-col gap-5">
        {/* Metadata */}
        <div className={sectionCard}>
          <h2 className="text-body font-semibold text-gray-900 mb-3">Metadata</h2>
          <div>
            {dlRow('Display Name', metadata.displayName)}
            {dlRow('Key', <span className="font-mono text-meta">{metadata.key}</span>)}
            {dlRow('Status', <span className={`px-2.5 py-0.5 rounded-full text-meta font-semibold capitalize ${metadata.status === 'active' ? 'bg-success-light text-success-dark' : 'bg-gray-100 text-gray-500'}`}>{metadata.status}</span>)}
            {dlRow('Category', metadata.category || 'N/A')}
            {dlRow('Version', metadata.version || 'N/A')}
            {dlRow('Author', metadata.author || 'N/A')}
            {dlRow('Description', metadata.description || 'N/A')}
            {dlRow('Tags', <div className="flex flex-wrap gap-1.5">{tags.map((t: string) => <span key={t} className="px-2.5 py-0.5 bg-blue-50 text-blue-700 rounded-full text-meta font-medium">{t}</span>)}</div>)}
            {dlRow('Created', new Date(metadata.createdAt).toLocaleString())}
            {dlRow('Updated', new Date(metadata.updatedAt).toLocaleString())}
          </div>
        </div>

        {/* Package info */}
        <div className={sectionCard}>
          <h2 className="text-body font-semibold text-gray-900 mb-3">Package Info</h2>
          <div>
            {dlRow('Manifest ID', <span className="font-mono text-meta">{pkg.manifest.id}</span>)}
            {dlRow('Manifest Name', pkg.manifest.name)}
            {dlRow('Uploaded', new Date(pkg.uploadedAt).toLocaleString())}
            {dlRow('Uploaded By', <span className="font-mono text-meta">{pkg.uploadedBy}</span>)}
          </div>
        </div>

        {/* Variables */}
        <div className={sectionCard}>
          <h2 className="text-body font-semibold text-gray-900 mb-3">
            Variables ({pkg.schemas.jsonSchema.properties ? Object.keys(pkg.schemas.jsonSchema.properties).length : 0})
          </h2>
          <div className="flex flex-col gap-2">
            {pkg.schemas.jsonSchema.properties && Object.entries(pkg.schemas.jsonSchema.properties).map(([key, prop]: [string, any]) => (
              <div key={key} className="flex items-center justify-between py-2.5 border-b border-gray-100 last:border-0">
                <span className="text-label font-medium text-gray-900">{prop.title || key}</span>
                <div className="flex items-center gap-2">
                  <span className="px-2.5 py-0.5 bg-gray-100 text-gray-600 rounded-full text-meta font-mono">{prop.type}</span>
                  {prop.advanced && <span className="px-2.5 py-0.5 bg-info-light text-info-dark rounded-full text-meta font-semibold">Advanced</span>}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

